<!--
Copyright 2021 Carnegie Mellon University. All Rights Reserved.
Released under a MIT (SEI)-style license. See LICENSE.md in the project root for license information.
-->

<mat-sidenav-container autosize [hasBackdrop]="false">
  <mat-sidenav
    #sidenav
    mode="side"
    mwlResizable
    [style.min-width.px]="sidenavWidth"
    [style.max-width.px]="sidenavWidth"
    (resizing)="resizingFn($event)"
    (resizeEnd)="resizeEndFn($event)"
    [(opened)]="sidebarOpen"
    position="end"
  >
    @if (sidebarOpen) {
    <div class="grid">
      <div
        class="resize-handle-left"
        mwlResizeHandle
        [resizeEdges]="{ left: true }"
      ></div>
      <div class="d-flex flex-column justify-content-start align-items-stretch">
        <button
          mat-icon-button
          (click)="resetSidebar()"
          matTooltip="Close Side Bar"
        >
          <mat-icon
            class="mdi-24px"
            fontIcon="mdi-chevron-double-right"
          ></mat-icon>
        </button>
        @switch (sidebarView) { @case ('modules') {
        <div>
          @if (filename.endsWith('.tf')) {
          <cas-module-list
            [modules]="modules"
            [selectedModule]="selectedModule$ | async"
            [isEditing]="isEditing$ | async"
            (getModule)="getModuleFn($event)"
            (insertModule)="insertModuleFn($event)"
          ></cas-module-list>
          }
        </div>
        } @case ('versions') {
        <div>
          <cas-version-list
            [fileId]="fileId"
            [selectedVersionId]="
              selectedVersionForDiff ? selectedVersionForDiff.id : ''
            "
            (getVersion)="getVersionFn($event)"
            (revertToVersion)="revertToVersionFn($event)"
          ></cas-version-list>
        </div>
        } }
      </div>
    </div>
    }
  </mat-sidenav>

  <mat-sidenav-content #sidenavContent id="sidenavContent">
    <div class="d-flex flex-column flex-fill editor-container">
      <mat-toolbar class="editor-toolbar">
        <mat-toolbar-row
          class="editor-toolbar d-flex flex-row justify-content-end align-items-center gap-1"
        >
          <div class="breadcrumb-bar">
            {{ breadcrumbString }}
          </div>
          <div class="spacer"></div>
          @if (file && !file.administrativelyLocked) {
          <button
            mat-icon-button
            color="primary"
            class="align-self-center"
            [disabled]="!canAdminLock"
            (click)="adminLockFile()"
          >
            <mat-icon
              class="mdi-24px"
              fontIcon="mdi-lock-open-outline"
              matTooltip="Not administratively locked"
            ></mat-icon>
          </button>
          } @if (file && file.administrativelyLocked) {
          <button
            mat-icon-button
            color="primary"
            class="align-self-center"
            [disabled]="!canAdminLock"
            (click)="adminUnlockFile()"
          >
            <mat-icon
              class="mdi-24px"
              fontIcon="mdi-lock-outline"
              matTooltip="Administratively locked"
            ></mat-icon>
          </button>
          }
          <button
            mat-icon-button
            hotkeyAction="FILE_SAVE"
            color="primary"
            class="align-self-center"
            [disabled]="!canEdit || (isSaved$ | async)"
            (click)="saveFile()"
          >
            <mat-icon
              class="mdi-24px"
              fontIcon="mdi-content-save"
              matTooltip="Save Changes"
            ></mat-icon>
          </button>
          <button
            mat-icon-button
            hotkeyAction="DISCARD_CHANGES"
            color="primary"
            class="align-self-center"
            [disabled]="
              !canEdit || (file && file.editorContent === file.content)
            "
            (click)="discardFileChanges()"
          >
            <mat-icon
              class="mdi-24px"
              fontIcon="mdi-undo"
              matTooltip="Discard Changes"
            ></mat-icon>
          </button>
          @if (file && file.lockedById && file.lockedById !== currentUserId) {
          <button mat-icon-button class="align-self-center" color="warn" (click)="canManage && forceUnlockFile()"
            [matTooltip]="
              canManage
                ? 'Force release the file from ' + file.lockedByName
                : 'This file is being edited by ' + file.lockedByName
            ">
            <mat-icon class="mdi-24px" fontIcon="mdi-account-edit"></mat-icon>
          </button>
          } @if (file && !file.lockedById) {
          <button
            mat-icon-button
            hotkeyAction="FILE_LOCK_TOGGLE"
            class="align-self-center"
            color="primary"
            (click)="lockFile()"
            [disabled]="!canEdit"
            [matTooltip]="
              canEdit
                ? 'Edit this file. Only one person can edit a file at a time'
                : 'You do not have permissions to edit this file'
            "
          >
            <mat-icon class="mdi-24px" fontIcon="mdi-square-edit-outline"></mat-icon>
          </button>
          } @if (file && file.lockedById === currentUserId) {
          <button
            mat-icon-button
            hotkeyAction="FILE_LOCK_TOGGLE"
            class="align-self-center"
            [disabled]="file.editorContent !== file.content"
            color="warn"
            (click)="unlockFile()"
            matTooltip="Finish editing and allow someone else to edit this file"
          >
            <mat-icon class="mdi-24px" fontIcon="mdi-square-edit-outline"></mat-icon>
          </button>
          }
          <mat-divider vertical style="height: 100%"></mat-divider>
          @if (filename.endsWith('.tf')) {
          <div>
            <button
              mat-icon-button
              hotkeyAction="FILE_MODULES"
              class="align-self-center"
              color="primary"
              (click)="
                sidebarView = 'modules'; sidebarChangedFn('modules', true)
              "
              [disabled]="sidebarView === 'modules'"
              matTooltip="Show the module list"
            >
              <mat-icon class="mdi-24px" fontIcon="mdi-code-tags"></mat-icon>
            </button>
            <button
              mat-icon-button
              hotkeyAction="FILE_VERSIONS"
              class="align-self-center"
              color="primary"
              (click)="
                sidebarView = 'versions'; sidebarChangedFn('versions', true)
              "
              [disabled]="sidebarView === 'versions'"
              matTooltip="Show the file versions"
            >
              <mat-icon class="mdi-24px" fontIcon="mdi-history"></mat-icon>
            </button>
          </div>
          }
        </mat-toolbar-row>
      </mat-toolbar>

      @if (!selectedVersionForDiff) {
      <ngx-monaco-editor
        #editor
        id="editor"
        class="code-editor"
        [options]="editorOptions"
        (onInit)="editorInit($event)"
        [(ngModel)]="code"
        (ngModelChange)="codeChangedEventHandler($event)"
      >
      </ngx-monaco-editor>
      } @if (selectedVersionForDiff) {
      <mat-toolbar class="version-toolbar">
        <mat-toolbar-row
          class="version-toolbar d-flex flex-row justify-content-between align-items-center"
        >
          <span class="left-version-text"> CURRENT </span>
          <span class="right-version-text">
            {{ selectedVersionForDiff.dateSaved | date: 'yyyy-MM-dd HH:mm' }} by
            {{ selectedVersionForDiff.modifiedByName }} as
            {{ selectedVersionForDiff.name }}
          </span>
        </mat-toolbar-row>
      </mat-toolbar>
      } @if (selectedVersionForDiff) {
      <ngx-monaco-diff-editor
        class="code-editor"
        [options]="editorOptions"
        [originalModel]="{ code: code, language: detectedLanguage }"
        [modifiedModel]="{ code: selectedVersionForDiff.content, language: detectedLanguage }"
      >
      </ngx-monaco-diff-editor>
      }
    </div>
  </mat-sidenav-content>
</mat-sidenav-container>
